import { makeAutoObservable } from 'mobx';
import { amountViewToValue } from 'src/components/CustomersBar/CustomerTab/BankingTab/components/amountViewToValue';
import { ModelsState } from 'src/AppState/Models/ModelsState';
import { Amount } from 'src_common/common/amount/Amount';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { CurrencyType, moneySymbol } from 'src_common/common/amount/website-money/currency';
//@ts-expect-error
import ImmutablePropTypes from 'react-immutable-proptypes';
import { PaysafePaymentProvider } from 'src/AppState/ConfigState/types';
import { PaysafeBillingInfoSuccess, paysafeBillingInfoFieldsValidation } from './paysafeBillingInfoFieldsValidation';
import { PaymentIframeData, PaysafeError, PaysafeInstance, PaysafeResult } from './PaysafeFrameOptions';
import { CreatePaymentPaysafe } from 'src_server/types/paysafe/createPaymentPaysafe';
import { createPaymentPaysafe } from 'src_server/types/paysafe';
import {
    validateAmountPattern,
    validateAmountRequire,
    validatePositive,
} from 'src/domains/players/shared/validatorsNew/validateRequire';
import { ApiResponseWrapper } from 'src/utils-mobx/ApiResponseWrapper';
import { StaffAccountBasicDataType, StaffAccountWalletDataType } from 'src/AppState/sdk/staff';
import { Common } from 'src/AppState/Common';
import { ConfigState } from 'src/AppState/ConfigState/ConfigState';
import { validateMinAmount } from 'src/domains/players/shared/validatorsNew/validateMinMaxAmount';

interface BalancesType {
    currency: CurrencyType;
    balance: string;
    withdrawableBalance: string;
    newBalance: string;
}

type IframeClosedType = {
    isOpen: false;
    data: null;
};

type IframeOpenedType = {
    isOpen: true;
    data: PaymentIframeData;
};
export class PaysafeTabState {
    public errorMessage: string | null = null;
    public successMessage: boolean = false;
    public iframe: IframeOpenedType | IframeClosedType = { isOpen: false, data: null };

    public readonly depositAmount: FormInputState<string, Amount>;
    public readonly walletData: () => ApiResponseWrapper<StaffAccountWalletDataType | null>;
    public readonly basicData: () => ApiResponseWrapper<StaffAccountBasicDataType | null>;

    private configState: ConfigState;

    public constructor(
        private models: ModelsState,
        private customerId: number,
        private permissionGrants: ImmutablePropTypes.map,
        private paysafePaymentProviderConfig: PaysafePaymentProvider,
        public onQueryAudits: () => void,
        public common: Common
    ) {
        this.configState = ConfigState.get(this.common);
        this.walletData = (): ApiResponseWrapper<StaffAccountWalletDataType | null> =>
            this.models.getUserModel(this.customerId).walletData;

        this.basicData = (): ApiResponseWrapper<StaffAccountBasicDataType | null> =>
            this.models.getUserModel(this.customerId).basicData;

        this.depositAmount = FormInputState.new('')
            .map(validateAmountRequire)
            .map(validatePositive)
            .map(validateAmountPattern())
            .map(
                validateMinAmount(
                    new Amount(this.configState.config.minDepositAmount),
                    () => moneySymbol(this.walletData().data?.currency ?? 'XTS'),
                    'Minimum deposit amount is'
                )
            );

        makeAutoObservable(this);
    }

    public onChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const inputValue = e.target.value;
        const formatValue = amountViewToValue(inputValue);
        this.depositAmount.setValue(formatValue);
    };

    public handleAdditionsChange = (newAmount: Amount): void => {
        this.depositAmount.setValue(newAmount.value);
        this.depositAmount.setAsVisited();
    };

    public handleClear = (): void => {
        this.depositAmount.setValue('');
        this.depositAmount.setAsVisited();
    };

    public get isEmptyAmountInput(): boolean {
        return this.depositAmount.result.value.type === 'error' || this.depositAmount.value === '';
    }

    private mapIframeData = (
        singleUseToken: string,
        billingInfo: PaysafeBillingInfoSuccess['data'],
        amount: number
    ): PaymentIframeData => {
        return {
            customerId: billingInfo.merchantCustomerId,
            data: {
                currency: billingInfo.currency,
                amount: amount,
                singleUseCustomerToken: singleUseToken,
                customer: billingInfo.customer,
                billingAddress: billingInfo.billingAddress,
                merchantRefNum: `${billingInfo.merchantCustomerId}-${singleUseToken}`,
                simulator: 'EXTERNAL',
                environment: this.paysafePaymentProviderConfig.environment,
                buttonColor: '#66cc99',
                imageUrl: 'https://hosted.paysafe.com/checkout/resource/demo-store/images/logo.png',
                holderName: billingInfo.billingAddress.nickName,
                threeDs: {
                    merchantUrl: 'https://api.qa.paysafe.com/checkout/v2/index.html#/desktop',
                    deviceChannel: 'BROWSER',
                    messageCategory: 'PAYMENT',
                    transactionIntent: 'GOODS_OR_SERVICE_PURCHASE',
                    authenticationPurpose: 'PAYMENT_TRANSACTION',
                },
                displayPaymentMethods: ['card'],
                paymentMethodDetails: {
                    card: {
                        accountId:
                            billingInfo.currency === 'GBP'
                                ? this.paysafePaymentProviderConfig.cardAccounts.GBP
                                : this.paysafePaymentProviderConfig.cardAccounts.EUR,
                    },
                },
            },
        };
    };

    public handlePayment = async (requestData: CreatePaymentPaysafe): Promise<createPaymentPaysafe.TResponseType> => {
        return await this.common.trpcClient.client.paysafeRouter.createPayment.mutate(requestData);
    };

    public paysafeResultCallback =
        (paymentIframeData: PaymentIframeData) =>
        async (instance: PaysafeInstance | null, error: unknown, result: unknown): Promise<void> => {
            const defaultError = 'Error in transaction final step. More details in Payment Search';
            try {
                const parsedError = PaysafeError.safeParse(error);

                if (parsedError.success) {
                    console.error('paysafe-iframe-error', parsedError.data);
                    instance?.showFailureScreen(parsedError.data.displayMessage);
                    await this.common.trpcClient.client.paysafeRouter.failedPayment.mutate({
                        customerId: paymentIframeData.customerId,
                        body: {
                            error: parsedError.data,
                            merchantRefNum: paymentIframeData.data.merchantRefNum,
                            amount: this.configState.precision.newFromOld(paymentIframeData.data.amount).value,
                            currencyCode: paymentIframeData.data.currency,
                        },
                    });
                    this.errorMessage = this.mapErrorMessage('errorIframe');
                    return;
                }

                const parsedResult = PaysafeResult.safeParse(result);
                if (parsedResult.success) {
                    const handlePaymentResponse = await this.handlePayment({
                        customerId: paymentIframeData.customerId,
                        body: {
                            amount: this.configState.precision.newFromOld(paymentIframeData.data.amount).value,
                            currencyCode: paymentIframeData.data.currency,
                            description: 'Top-up',
                            keywords: ['Top-up'],
                            paymentHandleToken: parsedResult.data.paymentHandleToken,
                        },
                    });

                    if (handlePaymentResponse.responseStatus === 'error') {
                        const errrorCode = handlePaymentResponse.data.errors[0]?.code ?? 'errorIframe';
                        const error = this.mapErrorMessage(
                            errrorCode,
                            handlePaymentResponse.data.errors[0]?.message ?? ''
                        );
                        instance?.showFailureScreen(error);
                        this.errorMessage = this.mapErrorMessage(errrorCode, error);
                        return;
                    }

                    instance?.showSuccessScreen('Remember to always gamble responsibly');
                    this.onQueryAudits();
                    this.successMessage = true;
                    this.errorMessage = null;
                    return;
                }

                instance?.showFailureScreen(defaultError);
                return;
            } catch (e) {
                console.error('paysafe-iframe-catch-error', e);
                this.errorMessage = this.mapErrorMessage('unknown');
                instance?.close();
                this.closeIframe();
            }
        };

    private mapErrorMessage = (errorType: string, message?: string): string => {
        switch (errorType) {
            case 'invalid-data':
                return 'Some fields are invalid.';
            case 'minimum':
                return `Deposit amount can not be lower than defined ${this.configState.config.minDepositAmount} limit`;
            case 'limit-reached':
                return "Deposit can't exceed defined limit.";
            case 'limit-deposit-restrictio':
                return "Deposit can't exceed defined limit.";
            case 'deposits-not-allowed':
                return message === undefined ? 'Your account is suspended.' : message;
            case 'declined':
                return 'ERROR_DEPOSIT_DECLINED';
            case 'backoffice-deposit-restriction':
                return 'Deposit is not allowed when account is deposit restricted';
            case 'failed-deposit-restriction':
                return 'Deposit is not allowed when account is deposit restricted';
            case 'missing-fields':
                if (message === undefined) {
                    return '';
                }
                return `Please add customer ${message} to customer 'Personal account' details.`;
            case 'errorIframe':
                return 'Error in transaction final step. More details in Payment Search';
            case 'unknown':
                return 'Unknown error';
            case 'deposit-restriction':
                return 'Deposit is not allowed when account is deposit restricted';
            default:
                if (message !== undefined) {
                    return message;
                }
                return 'Unknown error happened';
        }
    };

    public paysafeStateCallback = (): void => {
        this.closeIframe();
    };

    public closeIframe = (): void => {
        this.iframe = {
            isOpen: false,
            data: null,
        };
    };

    public submitDepositForm = async (): Promise<void> => {
        this.depositAmount.setAsVisited();
        this.errorMessage = null;
        this.successMessage = false;

        if (this.depositAmount.result.value.type === 'error') {
            console.error('submitDepositForm - Input error');
            return;
        }

        const basicData = this.basicData().data;
        const oldFormatAmount = this.configState.precision.valueOldFormat(this.depositAmount.result.value.data);

        const billingInfo = paysafeBillingInfoFieldsValidation(basicData);
        if (billingInfo?.type !== 'ok') {
            console.error('submitDepositForm - No basic user data');
            return;
        }

        try {
            const verifyUserResponse = await this.common.trpcClient.client.paysafeRouter.verifyUser.mutate({
                merchantCustomerId: billingInfo.data.merchantCustomerId.toString(),
                ...billingInfo.data.customer,
            });

            if (verifyUserResponse.responseStatus === 'error') {
                throw new Error();
            }

            const date = new Date().toUTCString();
            const singleUseTokenResponse =
                await this.common.trpcClient.client.paysafeRouter.createSingleUseCustomerToken.mutate({
                    externalCustomerId: verifyUserResponse.response.data.externalId,
                    body: {
                        merchantRefNum: `${billingInfo.data.merchantCustomerId.toString()}-${date}`,
                        paymentMethod: ['card'],
                    },
                });

            if (singleUseTokenResponse.responseStatus === 'error') {
                throw new Error();
            }

            this.iframe = {
                isOpen: true,
                data: this.mapIframeData(
                    singleUseTokenResponse.response.data.singleUseCustomerToken,
                    billingInfo.data,
                    oldFormatAmount
                ),
            };
            return;
        } catch (e) {
            console.error(e);
        }
    };

    public get getBalances(): BalancesType {
        const wallet = this.walletData().data;

        if (wallet === null) {
            return {
                currency: 'XTS',
                balance: new Amount('0').format('XTS'),
                withdrawableBalance: new Amount('0').format('XTS'),
                newBalance: new Amount('0').format('XTS'),
            };
        }

        return {
            currency: wallet.currency,
            balance: new Amount(wallet.balance).format(wallet.currency),
            withdrawableBalance: new Amount(wallet.withdrawableBalance).format(wallet.currency),
            newBalance:
                this.depositAmount.result.value.type === 'ok'
                    ? new Amount(wallet.balance).add(this.depositAmount.result.value.data).format(wallet.currency)
                    : new Amount(wallet.balance).format(wallet.currency),
        };
    }

    public get getPermission(): boolean {
        const permission = 'banking/deposits';

        if (this.permissionGrants?.size !== undefined) {
            for (let i = 0; i < this.permissionGrants.size; i++) {
                if (this.permissionGrants.getIn([i, 'permission']) === permission) {
                    return false;
                }
            }
        }

        return true;
    }
}
